import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { SyncStatusIndicator } from "../types/SyncQueue";

interface SyncContextValue {
  status: SyncStatusIndicator;
  updateStatus: (updates: Partial<SyncStatusIndicator>) => void;
}

const SyncContext = createContext<SyncContextValue | null>(null);

export const SyncProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<SyncStatusIndicator>({
    syncedCount: 0,
    pendingCount: 0,
    errorCount: 0,
    syncingCount: 0,
    isOnline: navigator.onLine,
  });

  useEffect(() => {
    const handleOnline = () => setStatus((prev) => ({ ...prev, isOnline: true }));
    const handleOffline = () => setStatus((prev) => ({ ...prev, isOnline: false }));

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const updateStatus = (updates: Partial<SyncStatusIndicator>) => {
    setStatus((prev) => ({ ...prev, ...updates }));
  };

  return (
    <SyncContext.Provider value={{ status, updateStatus }}>
      {children}
    </SyncContext.Provider>
  );
};

export const useSyncContext = () => {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error("useSyncContext must be used within a SyncProvider");
  }
  return context;
};
